/**
 * Per-client plans and usage limits (SPEC §18.1, decision 2026-10-22): Hangar meters and limits usage per org, the way
 * Expo does per account and Bitrise per workspace. Malible sets each client's plan (the operator CLI today); the client
 * only reads it. A limit of `null` is unlimited. Invoicing stays outside Hangar: the monthly CSV export is its input.
 */
import { z } from 'zod';
import { ConfigError, optionalEnv } from '../env.js';

export const OverQuotaPolicy = z.enum(['block', 'warn']);
export type OverQuotaPolicy = z.infer<typeof OverQuotaPolicy>;

/** Upper bounds a plan may carry (a typo such as an extra zero is caught at the CLI, not in an invoice). */
export const USAGE_PLAN_MAX = { monthlyBuildMinutes: 10_000_000, monthlyBuilds: 10_000_000, maxConcurrentBuilds: 1_000, artifactStorageGb: 1_000_000 } as const;

const count = (max: number) => z.number().int().min(1).max(max).nullable();

/**
 * The limits of one plan. Storage is in GiB (1024³ bytes) of mirrored artifacts that are still stored; minutes are
 * provider build minutes (billable minutes: rounded up per build); builds count the builds that reached a provider.
 */
export const UsagePlanFields = z.object({
  name: z.string().trim().min(1).max(60),
  monthlyBuildMinutes: count(USAGE_PLAN_MAX.monthlyBuildMinutes),
  monthlyBuilds: count(USAGE_PLAN_MAX.monthlyBuilds),
  maxConcurrentBuilds: count(USAGE_PLAN_MAX.maxConcurrentBuilds),
  artifactStorageGb: z.number().positive().max(USAGE_PLAN_MAX.artifactStorageGb).nullable(),
  overQuota: OverQuotaPolicy,
}).strict();
export type UsagePlanFields = z.infer<typeof UsagePlanFields>;

/**
 * What a new org gets until Malible sets its plan: sensible numbers, no prices, and `warn` so an existing client is never
 * blocked by a default nobody chose. Overridable per deployment (`defaultUsagePlanFromEnv`); Ranjeet and Anuj set the real ones.
 */
export const DEFAULT_USAGE_PLAN: UsagePlanFields = {
  name: 'standard', monthlyBuildMinutes: 3_000, monthlyBuilds: 1_000, maxConcurrentBuilds: 4, artifactStorageGb: 50, overQuota: 'warn',
};

/** The usage ratio from which a meter warns (SPEC §18.1): 80 %. */
export const USAGE_WARN_RATIO = 0.8;

function limitFromEnv(name: string, env: NodeJS.ProcessEnv, fallback: number | null, max: number, integer: boolean): number | null {
  const raw = optionalEnv(name, env);
  if (raw === undefined) return fallback;
  if (raw.toLowerCase() === 'unlimited') return null;
  const value = Number(raw);
  const valid = Number.isFinite(value) && value > 0 && value <= max && (!integer || Number.isInteger(value));
  if (!valid) throw new ConfigError(`${name} must be ${integer ? 'a whole number' : 'a number'} between ${integer ? 1 : '>0 and'} ${max}, or "unlimited"`);
  return value;
}

/**
 * The deployment's default plan: `HANGAR_PLAN_DEFAULT_NAME`, `_BUILD_MINUTES`, `_BUILDS`, `_MAX_CONCURRENT`, `_STORAGE_GB`
 * (a number or `unlimited`) and `_OVER_QUOTA` (`block` or `warn`); anything unset keeps `DEFAULT_USAGE_PLAN`'s value.
 */
export function defaultUsagePlanFromEnv(env: NodeJS.ProcessEnv = process.env): UsagePlanFields {
  const d = DEFAULT_USAGE_PLAN;
  const overQuota = optionalEnv('HANGAR_PLAN_DEFAULT_OVER_QUOTA', env);
  if (overQuota !== undefined && !OverQuotaPolicy.safeParse(overQuota).success) throw new ConfigError('HANGAR_PLAN_DEFAULT_OVER_QUOTA must be "block" or "warn"');
  const name = optionalEnv('HANGAR_PLAN_DEFAULT_NAME', env) ?? d.name;
  if (name.length > 60) throw new ConfigError('HANGAR_PLAN_DEFAULT_NAME must be at most 60 characters');
  return {
    name,
    monthlyBuildMinutes: limitFromEnv('HANGAR_PLAN_DEFAULT_BUILD_MINUTES', env, d.monthlyBuildMinutes, USAGE_PLAN_MAX.monthlyBuildMinutes, true),
    monthlyBuilds: limitFromEnv('HANGAR_PLAN_DEFAULT_BUILDS', env, d.monthlyBuilds, USAGE_PLAN_MAX.monthlyBuilds, true),
    maxConcurrentBuilds: limitFromEnv('HANGAR_PLAN_DEFAULT_MAX_CONCURRENT', env, d.maxConcurrentBuilds, USAGE_PLAN_MAX.maxConcurrentBuilds, true),
    artifactStorageGb: limitFromEnv('HANGAR_PLAN_DEFAULT_STORAGE_GB', env, d.artifactStorageGb, USAGE_PLAN_MAX.artifactStorageGb, false),
    overQuota: (overQuota as OverQuotaPolicy | undefined) ?? d.overQuota,
  };
}

/** A plan as stored on `organizations.plan` (the fields plus who changed it, and an optional temporary override). */
export interface StoredUsagePlan extends Partial<UsagePlanFields> {
  /** An operator-granted override: until this instant a `block` plan lets new builds through (never retroactive). */
  allowUntil?: Date;
  allowReason?: string;
  updatedAt?: Date;
  updatedBy?: string;
}

/** The plan in force: the stored one over the deployment default, field by field (a stored `null` is unlimited, not "unset"). */
export interface EffectiveUsagePlan extends UsagePlanFields {
  /** `org` when Malible set a plan for this org, `default` when the deployment default applies. */
  source: 'org' | 'default';
  allowUntil: Date | null;
  allowReason: string | null;
}

export function effectiveUsagePlan(stored: StoredUsagePlan | null | undefined, defaults: UsagePlanFields = DEFAULT_USAGE_PLAN): EffectiveUsagePlan {
  if (!stored) return { ...defaults, source: 'default', allowUntil: null, allowReason: null };
  const pick = <K extends keyof UsagePlanFields>(key: K): UsagePlanFields[K] => (stored[key] !== undefined ? (stored[key] as UsagePlanFields[K]) : defaults[key]);
  return {
    name: pick('name'), monthlyBuildMinutes: pick('monthlyBuildMinutes'), monthlyBuilds: pick('monthlyBuilds'), maxConcurrentBuilds: pick('maxConcurrentBuilds'),
    artifactStorageGb: pick('artifactStorageGb'), overQuota: pick('overQuota'),
    source: 'org', allowUntil: stored.allowUntil ?? null, allowReason: stored.allowReason ?? null,
  };
}
